import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Pagination,
  InputAdornment,
  Button,
  Avatar,
  Chip,
  Rating,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import axios from 'axios';
import { debounce } from 'lodash';

// Theme Colors
const themeColors = {
  primary: '#1976D2', // Blue
  secondary: '#4CAF50', // Green
  background: '#F9FAFB', // Light grey
  cardBackground: '#FFFFFF', // White cards
  grey: '#686978',
  borderColor: '#E0E0E0',
};

// API Config
const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';
const AUTH_TOKEN = '045e6c0d-8ed7-480e-820e-6b8f4261cb51';

const DoctorList = () => {
  const [doctors, setDoctors] = useState([]); // Doctor data
  const [loading, setLoading] = useState(false); // Loading state
  const [searchQuery, setSearchQuery] = useState(''); // Search query
  const [currentPage, setCurrentPage] = useState(1); // Current page
  const [totalPages, setTotalPages] = useState(1); // Total pages
  const limit = 10; // Items per page

  // Fetch Doctors API with debounce
  const fetchDoctors = debounce(async (query, page) => {
    setLoading(true);
    const offset = (page - 1) * limit;

    try {
      const response = await axios.get(
        `${BASE_URL}/doctors?query=${query || '*'}&offset=${offset}&limit=${limit}&latitude=26.8505899&longitude=75.7909157`,
        {
          headers: { Authorization: AUTH_TOKEN },
        }
      );

      const data = response.data;
      if (data.status) {
        setDoctors(data.data);
        setTotalPages(Math.ceil(data.data.length / limit));
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  }, 500);

  // Initial fetch
  useEffect(() => {
    fetchDoctors('', 1);
  }, []);

  // Handle search input
  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    setCurrentPage(1);
    fetchDoctors(query, 1);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    fetchDoctors(searchQuery, value);
  };

  return (
    <Box sx={{ backgroundColor: themeColors.background, minHeight: '100vh', padding: '20px' }}>
      {/* Search Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '20px',
          padding: '8px',
          backgroundColor: themeColors.cardBackground,
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <TextField
          fullWidth
          placeholder="Search doctors..."
          value={searchQuery}
          onChange={handleSearch}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: themeColors.grey }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Loading State */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Doctor Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '20px',
            }}
          >
            {doctors.map((doctor) => (
              <Card
                key={doctor.reference_id}
                sx={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.3s',
                  '&:hover': { transform: 'scale(1.03)' },
                }}
              >
                <CardContent>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <Avatar sx={{ bgcolor: themeColors.primary, marginRight: '10px' }}>
                      {doctor.initials}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {doctor.name}
                      </Typography>
                      <Typography variant="body2" color={themeColors.grey}>
                        {doctor.specializations}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Experience and Rating */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Typography variant="body2" color={themeColors.grey}>
                      {doctor.experience}
                    </Typography>
                    <Rating value={doctor.rating} readOnly size="small" />
                  </Box>

                  <Divider sx={{ marginBottom: '12px' }} />

                  {/* Organization Details */}
                  {doctor.organisations.map((org) => (
                    <Box key={org.reference_id} sx={{ marginBottom: '12px' }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {org.name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color={themeColors.grey}>
                        {org.address}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocationOnIcon fontSize="small" sx={{ marginRight: '4px' }} />
                        {org.distance.value} {org.distance.unit} away
                      </Typography>
                      {org.consultation_fee && (
                        <Typography variant="body2" sx={{ marginTop: '8px' }}>
                          ₹{org.consultation_fee} Consultation Fee
                        </Typography>
                      )}

                      {/* Timings */}
                      {org.timing && (
                        <Box sx={{ marginTop: '8px' }}>
                          {org.timing.map((time, index) => (
                            <Typography key={index} variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                              <AccessTimeIcon fontSize="small" sx={{ marginRight: '4px' }} />
                              {time.day}: {time.time}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}

                  {/* Contact Button */}
                  <Button
                    variant="contained"
                    startIcon={<PhoneIcon />}
                    sx={{ backgroundColor: themeColors.primary, width: '100%', marginTop: '8px' }}
                  >
                    Contact Now
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Pagination */}
          <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} color="primary" />
          </Box>
        </>
      )}
    </Box>
  );
};

export default DoctorList;